import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  OPCOS,
  Sidebar,
  TopBar,
  type OpcoDescriptor,
  type OpcoId,
  type SidebarSection,
  type Theme,
} from '@silverfalls/sterling';
import { ToastProvider } from './components/Toast.js';
import { OpcoProvider, useOpco } from './context/OpcoContext.js';
import { APInvoiceDetail } from './screens/APInvoiceDetail.js';
import { APInvoices } from './screens/APInvoices.js';
import { PaymentConfigSettings } from './screens/PaymentConfigSettings.js';
import { PaymentRunDetail } from './screens/PaymentRunDetail.js';
import { PaymentRuns } from './screens/PaymentRuns.js';
import { PurchaseOrderDetail } from './screens/PurchaseOrderDetail.js';
import { PurchaseOrders } from './screens/PurchaseOrders.js';
import { ReceiptDetail } from './screens/ReceiptDetail.js';
import { Receipts } from './screens/Receipts.js';
import { Report1099 } from './screens/Report1099.js';
import { RequisitionDetail } from './screens/RequisitionDetail.js';
import { Requisitions } from './screens/Requisitions.js';
import { VendorDetail } from './screens/VendorDetail.js';
import { Vendors } from './screens/Vendors.js';

// Anvil registers itself in Sterling's app suite as `anvil-erp`. The slug
// lights up the suite launcher's current-app indicator.
const ANVIL_APP_SLUG = 'anvil-erp';
const ANVIL_APP_NAME = 'Anvil ERP';

// Anvil's 4 active OPCO short codes ↔ Sterling-canonical OPCO ids.
// Silver Falls Capital is the parent holding co; not in Anvil's switcher
// but kept as a fallback for chrome rendering on initial boot.
const ANVIL_OPCO_TO_STERLING: Readonly<Record<string, OpcoId>> = {
  SWG: 'sportworks',
  OCA: 'omni',
  BB: 'birdbuffer',
  CITC: 'citc',
};
const STERLING_OPCO_TO_ANVIL: Readonly<Record<OpcoId, string>> = {
  sportworks: 'SWG',
  omni: 'OCA',
  birdbuffer: 'BB',
  citc: 'CITC',
  'silver-falls': '',
};

// Visible OPCOs in the switcher menu — operating businesses only, parent
// hidden per Anvil's product convention.
const SWITCHER_OPCOS: ReadonlyArray<OpcoDescriptor> = OPCOS.filter(
  (o) => o.id !== 'silver-falls',
);

// Theme is persisted per-user. The light/dark toggle in the TopBar writes
// through this. Falls back to "light" on first boot or in incognito.
const THEME_LS_KEY = 'anvil.theme';
const SIDEBAR_LS_KEY = 'anvil.sidebarCollapsed';

interface NavRoute {
  readonly id: string;
  readonly path: string;
  readonly label: string;
  readonly icon:
    | 'user'
    | 'clipboard-check'
    | 'file-text'
    | 'inbox'
    | 'package'
    | 'download'
    | 'archive'
    | 'settings';
}

const PROCURE_TO_PAY_NAV: ReadonlyArray<NavRoute> = [
  { id: 'vendors',         path: '/vendors',         label: 'Vendors',          icon: 'user' },
  { id: 'requisitions',    path: '/requisitions',    label: 'Requisitions',     icon: 'clipboard-check' },
  { id: 'purchase-orders', path: '/purchase-orders', label: 'Purchase orders',  icon: 'file-text' },
  { id: 'receipts',        path: '/receipts',        label: 'Receipts',         icon: 'inbox' },
  { id: 'ap-invoices',     path: '/ap-invoices',     label: 'AP invoices',      icon: 'package' },
  { id: 'payment-runs',    path: '/payment-runs',    label: 'Payment runs',     icon: 'archive' },
];

const REPORTS_NAV: ReadonlyArray<NavRoute> = [
  { id: 'report-1099', path: '/reports/1099', label: '1099 reporting', icon: 'download' },
];

const SETTINGS_NAV: ReadonlyArray<NavRoute> = [
  {
    id: 'payment-config',
    path: '/settings/payment-config',
    label: 'Payment config',
    icon: 'settings',
  },
];

const ALL_NAV: ReadonlyArray<NavRoute> = [
  ...PROCURE_TO_PAY_NAV,
  ...REPORTS_NAV,
  ...SETTINGS_NAV,
];

// Maps the current URL pathname to a nav item id. Detail routes resolve to
// their parent index nav item.
function activeNavId(pathname: string): string {
  for (const route of ALL_NAV) {
    if (pathname === route.path || pathname.startsWith(`${route.path}/`)) {
      return route.id;
    }
  }
  return PROCURE_TO_PAY_NAV[0]!.id;
}

export function App(): JSX.Element {
  return (
    <OpcoProvider>
      <ToastProvider>
        <Shell />
      </ToastProvider>
    </OpcoProvider>
  );
}

function Shell(): JSX.Element {
  const { active, setActive } = useOpco();
  const navigate = useNavigate();
  const location = useLocation();

  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem(THEME_LS_KEY);
      return stored === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  const [opcoMenuOpen, setOpcoMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_LS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Wire theme + opco through the Sterling resolution model:
  //   <html data-theme="light|dark">
  //   <body data-opco="sportworks|omni|birdbuffer|citc">
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_LS_KEY, theme);
    } catch {
      // localStorage unavailable — fall back to in-memory.
    }
  }, [theme]);

  useEffect(() => {
    const body = document.body;
    if (active) {
      const sterlingId = ANVIL_OPCO_TO_STERLING[active.shortCode];
      if (sterlingId) {
        body.setAttribute('data-opco', sterlingId);
        return;
      }
    }
    // No OPCO selected yet (initial boot before context loads) — paint
    // chrome with silver-falls so the TopBar isn't transparent.
    body.setAttribute('data-opco', 'silver-falls');
  }, [active]);

  const onToggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const onToggleCollapse = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_LS_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const onPickOpco = useCallback(
    (id: OpcoId) => {
      const shortCode = STERLING_OPCO_TO_ANVIL[id];
      if (shortCode) setActive(shortCode);
      setOpcoMenuOpen(false);
    },
    [setActive],
  );

  const onPickNav = useCallback(
    (id: string) => {
      const route = ALL_NAV.find((r) => r.id === id);
      if (route) navigate(route.path);
    },
    [navigate],
  );

  // Derive the Sterling OpcoDescriptor for the TopBar. Falls back to
  // silver-falls so the bar always paints; the switcher list excludes it.
  const sterlingOpco: OpcoDescriptor = useMemo(() => {
    const sterlingId = active ? ANVIL_OPCO_TO_STERLING[active.shortCode] : undefined;
    return (
      OPCOS.find((o) => o.id === sterlingId) ??
      OPCOS.find((o) => o.id === 'silver-falls')!
    );
  }, [active]);

  const sections: ReadonlyArray<SidebarSection> = useMemo(
    () => [
      {
        title: 'Procure-to-pay',
        items: PROCURE_TO_PAY_NAV.map((r) => ({
          id: r.id,
          label: r.label,
          icon: r.icon,
        })),
      },
      {
        title: 'Reports',
        items: REPORTS_NAV.map((r) => ({
          id: r.id,
          label: r.label,
          icon: r.icon,
        })),
      },
      {
        title: 'Settings',
        items: SETTINGS_NAV.map((r) => ({
          id: r.id,
          label: r.label,
          icon: r.icon,
        })),
      },
    ],
    [],
  );

  const activeId = activeNavId(location.pathname);

  return (
    <div className="sterling-shell">
      <TopBar
        appName={ANVIL_APP_NAME}
        currentAppSlug={ANVIL_APP_SLUG}
        opco={sterlingOpco}
        opcoList={SWITCHER_OPCOS}
        opcoMenuOpen={opcoMenuOpen}
        onToggleOpcoMenu={() => setOpcoMenuOpen((prev) => !prev)}
        onCloseOpcoMenu={() => setOpcoMenuOpen(false)}
        onPickOpco={onPickOpco}
        theme={theme}
        onToggleTheme={onToggleTheme}
        hideSearch
        userInitials="A"
        userName="Anvil user"
      />

      <div className="sterling-body">
        <Sidebar
          active={activeId}
          onPick={onPickNav}
          collapsed={sidebarCollapsed}
          onToggleCollapse={onToggleCollapse}
          sections={[...sections]}
        />

        <main className="sterling-canvas">
          <Routes>
            <Route path="/" element={<Navigate to="/vendors" replace />} />
            <Route
              path="/vendors"
              element={active ? <Vendors opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route path="/vendors/:ref" element={<VendorDetail />} />
            <Route
              path="/purchase-orders"
              element={active ? <PurchaseOrders opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route path="/purchase-orders/:ref" element={<PurchaseOrderDetail />} />
            <Route
              path="/requisitions"
              element={active ? <Requisitions opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route path="/requisitions/:ref" element={<RequisitionDetail />} />
            <Route
              path="/receipts"
              element={active ? <Receipts opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route path="/receipts/:ref" element={<ReceiptDetail />} />
            <Route
              path="/ap-invoices"
              element={active ? <APInvoices opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route path="/ap-invoices/:ref" element={<APInvoiceDetail />} />
            <Route
              path="/payment-runs"
              element={active ? <PaymentRuns opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route path="/payment-runs/:ref" element={<PaymentRunDetail />} />
            <Route
              path="/reports/1099"
              element={active ? <Report1099 opcoRef={active.shortCode} /> : <NoOpco />}
            />
            <Route
              path="/settings/payment-config"
              element={
                active ? <PaymentConfigSettings opcoRef={active.shortCode} /> : <NoOpco />
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function NoOpco(): JSX.Element {
  return <div className="empty-state">No OPCO selected.</div>;
}

function NotFound(): JSX.Element {
  return <div className="empty-state">Page not found.</div>;
}
